<template>
  <div class="actividades-detalle">
    <!-- Header -->
    <div class="content-header">
      <div class="content-header-left">
        <h1 class="content-header-title">{{ actividad.tipoActividad?.nombre || 'Cargando...' }}</h1>
        <p class="content-header-subtitle">{{ actividad.observaciones || 'Sin observaciones' }}</p>
      </div>
      <div class="content-header-right">
        <b-button-group>
          <b-button variant="outline-info" @click="agregarRecordatorio">
            <feather-icon icon="CalendarIcon" size="16" class="mr-50" />
            Recordatorio
          </b-button>
          <b-button variant="outline-warning" @click="editarActividad">
            <feather-icon icon="EditIcon" size="16" class="mr-50" />
            Editar
          </b-button>
          <b-button variant="outline-secondary" @click="volver">
            <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50" />
            Volver
          </b-button>
        </b-button-group>
      </div>
    </div>

    <div v-if="cargando" class="text-center py-5">
      <b-spinner class="text-primary"></b-spinner>
      <p class="mt-2">Cargando actividad...</p>
    </div>

    <div v-else-if="actividad.idActividad">
      <!-- Información General -->
      <b-row>
        <b-col lg="8">
          <b-card title="Información General" class="mb-4">
            <b-row>
              <b-col md="6">
                <div class="info-item">
                  <label>Estado:</label>
                  <b-badge :variant="getEstadoVariant(actividad.estadoActividad?.nombre)" class="text-capitalize">
                    {{ actividad.estadoActividad?.nombre || 'Sin estado' }}
                  </b-badge>
                </div>
              </b-col>
              <b-col md="6">
                <div class="info-item">
                  <label>Tipo de Actividad:</label>
                  <span>{{ actividad.tipoActividad?.nombre || 'Sin tipo' }}</span>
                </div>
              </b-col>
            </b-row>

            <b-row class="mt-3">
              <b-col md="6">
                <div class="info-item">
                  <label>Fecha:</label>
                  <span>{{ formatearFecha(actividad.fecha) }}</span>
                </div>
              </b-col>
              <b-col md="6">
                <div class="info-item">
                  <label>Próxima Acción:</label>
                  <span>{{ formatearFecha(actividad.proximaAccionFecha) }}</span>
                </div>
              </b-col>
            </b-row>

            <b-row class="mt-3">
              <b-col md="6">
                <div class="info-item">
                  <label>Tipo de Próxima Acción:</label>
                  <span>{{ actividad.proximaAccionTipo?.nombre || 'Sin próxima acción' }}</span>
                </div>
              </b-col>
              <b-col md="6">
                <div class="info-item">
                  <label>Recordatorio:</label>
                  <b-badge :variant="actividad.recordatorio ? 'success' : 'secondary'">
                    {{ actividad.recordatorio ? 'Activado' : 'Desactivado' }}
                  </b-badge>
                </div>
              </b-col>
            </b-row>

            <div class="info-item mt-3">
              <label>Observaciones:</label>
              <p class="mt-1">{{ actividad.observaciones || 'Sin observaciones' }}</p>
            </div>
          </b-card>

          <!-- Información del Proyecto -->
          <b-card title="Proyecto Asociado" class="mb-4" v-if="actividad.idProyecto">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <h6 class="mb-0">Información del Proyecto</h6>
              <b-button 
                variant="outline-primary" 
                size="sm" 
                @click="irAlProyecto"
                v-if="actividad.idProyecto"
              >
                <feather-icon icon="ExternalLinkIcon" size="14" class="mr-1" />
                Ver Proyecto
              </b-button>
            </div>
            
            <b-row>
              <b-col md="12">
                <div class="info-item">
                  <label>Nombre del Proyecto:</label>
                  <span class="project-name">{{ actividad.nombreProyecto || 'Sin nombre' }}</span>
                </div>
              </b-col>
            </b-row>
            
            <b-row class="mt-3">
              <b-col md="6">
                <div class="info-item">
                  <label>Fecha de Inicio:</label>
                  <span>{{ formatearFecha(actividad.fechaInicioProyecto) }}</span>
                </div>
              </b-col>
              <b-col md="6">
                <div class="info-item">
                  <label>Fecha de Fin:</label>
                  <span>{{ formatearFecha(actividad.fechaFinProyecto) }}</span>
                </div>
              </b-col>
            </b-row>
            
            <b-row class="mt-3" v-if="actividad.nombreEstadoProyecto">
              <b-col md="6">
                <div class="info-item">
                  <label>Estado del Proyecto:</label>
                  <b-badge :variant="getEstadoVariant(actividad.nombreEstadoProyecto)" class="text-capitalize">
                    {{ actividad.nombreEstadoProyecto }}
                  </b-badge>
                </div>
              </b-col>
            </b-row>
            
            <div v-if="actividad.descripcionProyecto" class="info-item mt-3">
              <label>Descripción del Proyecto:</label>
              <p class="mt-1">{{ actividad.descripcionProyecto }}</p>
            </div>
          </b-card>
          
          <!-- Mensaje si no hay proyecto asociado -->
          <b-card title="Proyecto Asociado" class="mb-4" v-else>
            <div class="text-center text-muted py-3">
              <feather-icon icon="FolderIcon" size="24" class="mb-2" />
              <p>Esta actividad no tiene un proyecto asociado</p>
            </div>
          </b-card>

          <!-- Documentos -->
          <b-card title="Documentos" class="mb-4">
            <template #header>
              <div class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">Documentos</h6>
                <b-button 
                  variant="outline-primary" 
                  size="sm" 
                  @click="mostrarSubidaDocumentos = !mostrarSubidaDocumentos"
                >
                  <feather-icon icon="UploadIcon" size="14" class="mr-1" />
                  {{ mostrarSubidaDocumentos ? 'Ocultar' : 'Subir' }} Documento
                </b-button>
              </div>
            </template>

            <!-- Formulario de subida de documentos -->
            <div v-if="mostrarSubidaDocumentos" class="mb-4">
              <SubidaDocumentos 
                :id-actividad="actividad.idActividad"
                :id-usuario="usuarioActual.idUsuario"
                @documento-subido="onDocumentoSubido"
              />
            </div>

            <!-- Lista de documentos -->
            <div v-if="documentos && documentos.length === 0" class="text-center text-muted py-3">
              <feather-icon icon="FileIcon" size="24" class="mb-2" />
              <p>No hay documentos adjuntos</p>
            </div>
            <div v-else>
              <div
                v-for="documento in documentos"
                :key="documento.idDocumento"
                class="file-item"
              >
                <feather-icon icon="FileIcon" size="16" class="mr-2" />
                <div class="file-info">
                  <div class="file-name">{{ documento.nombreArchivo }}</div>
                  <div class="file-meta">
                    <small class="text-muted">
                      {{ documento.nombreCategoriaArchivo }} - v{{ documento.version }}
                    </small>
                  </div>
                </div>
                <div class="file-actions">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    class="mr-2"
                    @click="descargarDocumento(documento.idDocumento, documento.nombreArchivo)"
                  >
                    <feather-icon icon="DownloadIcon" size="14" />
                  </b-button>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    @click="eliminarDocumento(documento.idDocumento)"
                  >
                    <feather-icon icon="TrashIcon" size="14" />
                  </b-button>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>

        <!-- Sidebar -->
        <b-col lg="4">
          <!-- Información del Creador -->
          <b-card title="Creado por" class="mb-4">
            <div class="info-item">
              <label>Nombre:</label>
              <span>{{ actividad.creadoPor?.nombre || 'Sin información' }}</span>
            </div>
            <div class="info-item">
              <label>Email:</label>
              <span>{{ actividad.creadoPor?.email || 'Sin email' }}</span>
            </div>
          </b-card>

          <!-- Fechas del Sistema -->
          <b-card title="Fechas del Sistema">
            <div class="info-item">
              <label>Creado:</label>
              <span>{{ formatearFechaHora(actividad.createdAt) }}</span>
            </div>
            <div class="info-item">
              <label>Última Actualización:</label>
              <span>{{ formatearFechaHora(actividad.updatedAt) }}</span>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Error si no se encuentra la actividad -->
    <div v-else class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-warning mb-3"></feather-icon>
      <h4>Actividad no encontrada</h4>
      <p class="text-muted">La actividad que buscas no existe o ha sido eliminada.</p>
      <b-button variant="primary" @click="volver">
        Volver al listado
      </b-button>
    </div>
  </div>
</template>

<script>
import { ActividadService } from '@/services/actividadService'
import { DocumentoService } from '@/services/documentoService'
import SubidaDocumentos from '@/components/SubidaDocumentos.vue'
import { 
  generarEnlaceGoogleCalendar, 
  generarEnlaceOutlookCalendar, 
  generarEnlaceYahooCalendar, 
  descargarArchivoICS,
  generarArchivoICS,
  crearEventoDesdeActividad 
} from '@/utils/calendarUtils'

export default {
  name: 'ActividadesDetalle',
  components: {
    SubidaDocumentos
  },
  data() {
    return {
      cargando: true,
      actividad: {},
      documentos: [],
      mostrarSubidaDocumentos: false,
      usuarioActual: {
        idUsuario: 1 // TODO: Obtener del store de autenticación
      }
    }
  },
  mounted() {
    this.cargarActividad()
    this.cargarDocumentos()
  },
  methods: {
    async cargarActividad() {
      this.cargando = true
      try {
        const actividadId = parseInt(this.$route.params.id)
        this.actividad = await ActividadService.getById(actividadId)
      } catch (error) {
        console.error('Error al cargar la actividad:', error)
        this.$toast({
          component: {
            template: '<div>Error al cargar la actividad</div>',
          },
          props: {
            variant: 'danger',
          },
        })
        this.actividad = {}
      } finally {
        this.cargando = false
      }
    },
    getEstadoVariant(estado) {
      if (!estado) return 'secondary'
      
      const variants = {
        'En Progreso': 'primary',
        'Completado': 'success',
        'Pendiente': 'warning',
        'Cancelado': 'danger',
        'Activo': 'primary',
        'Inactivo': 'secondary',
        'Finalizado': 'success',
        'En Curso': 'primary',
        'Pausado': 'warning',
        'Terminado': 'success',
      }
      return variants[estado] || 'secondary'
    },
    formatearFecha(fecha) {
      if (!fecha) return 'N/A'
      return new Date(fecha).toLocaleDateString('es-ES')
    },
    formatearFechaHora(fecha) {
      if (!fecha) return 'N/A'
      return new Date(fecha).toLocaleString('es-ES')
    },
    editarActividad() {
      this.$router.push({ name: 'actividades-editar', params: { id: this.actividad.idActividad } })
    },
    volver() {
      this.$router.push({ name: 'actividades-lista' })
    },
    irAlProyecto() {
      if (this.actividad.idProyecto) {
        this.$router.push({ 
          name: 'proyectos-detalle', 
          params: { id: this.actividad.idProyecto } 
        })
      }
    },
    async cargarDocumentos() {
      try {
        const actividadId = parseInt(this.$route.params.id)
        this.documentos = await DocumentoService.getByActividad(actividadId)
      } catch (error) {
        console.error('Error al cargar documentos:', error)
        this.$toast({
          component: {
            template: '<div>Error al cargar los documentos</div>',
          },
          props: {
            variant: 'danger',
          },
        })
      }
    },
    async descargarDocumento(id, nombreArchivo) {
      try {
        this.$toast({
          message: 'Descargando documento...',
          variant: 'info',
        })
        
        // Usar el nuevo método que maneja el nombre original automáticamente
        await DocumentoService.downloadDocumento(id)
        
        this.$toast({
          message: 'Documento descargado correctamente',
          variant: 'success',
        })
      } catch (error) {
        console.error('Error al descargar documento:', error)
        this.$toast({
          message: 'Error al descargar el documento',
          variant: 'danger',
        })
      }
    },
    async eliminarDocumento(id) {
      try {
        await this.$swal({
          title: '¿Estás seguro?',
          text: 'No podrás revertir esta acción',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sí, eliminar',
          cancelButtonText: 'Cancelar',
          customClass: {
            confirmButton: 'btn btn-danger',
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
        })
        
        await DocumentoService.delete(id)
        
        this.$toast({
          component: {
            template: '<div>Documento eliminado correctamente</div>',
          },
          props: {
            variant: 'success',
          },
        })
        
        // Recargar la lista de documentos
        await this.cargarDocumentos()
        
      } catch (error) {
        if (error.dismiss) return // Usuario canceló
        
        console.error('Error al eliminar documento:', error)
        this.$toast({
          component: {
            template: '<div>Error al eliminar el documento</div>',
          },
          props: {
            variant: 'danger',
          },
        })
      }
    },
    async onDocumentoSubido() {
      // Recargar la lista de documentos cuando se sube uno nuevo
      await this.cargarDocumentos()
      this.mostrarSubidaDocumentos = false
    },
    async agregarRecordatorio() {
      // Primero pedir la hora del recordatorio
      const result = await this.$swal({
        title: 'Configurar Recordatorio',
        html: `
          <div class="text-left">
            <p>Selecciona la hora para el recordatorio:</p>
            <div class="form-group">
              <label for="hora">Hora:</label>
              <input type="time" id="hora" class="form-control" value="09:00" required>
            </div>
            <p class="text-muted small mt-2">Fecha: ${this.formatearFecha(this.actividad.proximaAccionFecha || this.actividad.fecha)}</p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Continuar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
        didOpen: () => {
          // Asegurar que el input esté enfocado
          setTimeout(() => {
            const input = document.getElementById('hora')
            if (input) input.focus()
          }, 100)
        },
        preConfirm: () => {
          const horaInput = document.getElementById('hora')
          if (!horaInput || !horaInput.value) {
            this.$swal.showValidationMessage('Por favor selecciona una hora')
            return false
          }
          return horaInput.value
        }
      })

      if (!result.value) return
      
      const horaRecordatorio = result.value

      // Crear el evento con la hora seleccionada
      const evento = crearEventoDesdeActividad(this.actividad, horaRecordatorio)
      
      this.$swal({
        title: 'Agregar Recordatorio',
        text: 'Selecciona cómo quieres agregar el recordatorio:',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Google Calendar',
        cancelButtonText: 'Cancelar',
        showDenyButton: true,
        denyButtonText: 'Outlook',
        showCloseButton: true,
        customClass: {
          confirmButton: 'btn btn-success',
          denyButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          // Google Calendar
          const url = generarEnlaceGoogleCalendar(evento)
          window.open(url, '_blank')
        } else if (result.isDenied) {
          // Outlook
          const url = generarEnlaceOutlookCalendar(evento)
          window.open(url, '_blank')
        }
      })
      
      // Mostrar opciones adicionales
      this.$swal({
        title: 'Otras opciones',
        text: 'También puedes descargar el archivo o enviarlo por correo:',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Descargar archivo',
        cancelButtonText: 'Cerrar',
        showDenyButton: true,
        denyButtonText: 'Enviar por correo',
        customClass: {
          confirmButton: 'btn btn-info',
          denyButton: 'btn btn-warning',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          descargarArchivoICS(evento)
        } else if (result.isDenied) {
          this.enviarRecordatorioPorCorreo(this.actividad, horaRecordatorio)
        }
      })
    },
    async enviarRecordatorioPorCorreo(actividad, horaRecordatorio) {
      const { value: emails } = await this.$swal({
        title: 'Enviar Recordatorio por Correo',
        html: `
          <div class="text-left">
            <p>Ingresa las direcciones de correo electrónico:</p>
            <div class="form-group">
              <label for="emails">Correos electrónicos:</label>
              <textarea 
                id="emails" 
                class="form-control" 
                rows="3" 
                placeholder="ejemplo1@correo.com, ejemplo2@correo.com, ejemplo3@correo.com"
                required
              ></textarea>
              <small class="form-text text-muted">Separa múltiples correos con comas</small>
            </div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Enviar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-warning',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
        preConfirm: () => {
          const emailsInput = document.getElementById('emails')
          if (!emailsInput || !emailsInput.value.trim()) {
            this.$swal.showValidationMessage('Debes ingresar al menos una dirección de correo')
            return false
          }
          
          const emails = emailsInput.value.split(',').map(email => email.trim()).filter(email => email)
          if (emails.length === 0) {
            this.$swal.showValidationMessage('Debes ingresar al menos una dirección de correo válida')
            return false
          }
          
          // Validar cada email
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          const emailsInvalidos = emails.filter(email => !emailRegex.test(email))
          if (emailsInvalidos.length > 0) {
            this.$swal.showValidationMessage(`Correos inválidos: ${emailsInvalidos.join(', ')}`)
            return false
          }
          
          return emails
        }
      })

      if (emails && emails.length > 0) {
        try {
          this.$swal({
            title: 'Enviando...',
            text: `Enviando recordatorio a ${emails.length} destinatario${emails.length > 1 ? 's' : ''}...`,
            icon: 'info',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
              this.$swal.showLoading()
            }
          })

          // Generar el evento y el archivo ICS con la hora seleccionada
          const evento = crearEventoDesdeActividad(actividad, horaRecordatorio)
          const archivoICS = generarArchivoICS(evento)
          
          // Enviar el archivo ICS por correo a múltiples destinatarios
          await this.enviarCorreoRecordatorio(actividad.idActividad, emails, archivoICS, evento)
          
          this.$swal({
            title: '¡Enviado!',
            text: `El recordatorio ha sido enviado a ${emails.length} destinatario${emails.length > 1 ? 's' : ''}`,
            icon: 'success',
            confirmButtonText: 'Aceptar',
            customClass: {
              confirmButton: 'btn btn-success',
            },
            buttonsStyling: false,
          })
        } catch (error) {
          console.error('Error al enviar el correo:', error)
          this.$swal({
            title: 'Error',
            text: 'No se pudo enviar el recordatorio. Inténtalo de nuevo.',
            icon: 'error',
            confirmButtonText: 'Aceptar',
            customClass: {
              confirmButton: 'btn btn-danger',
            },
            buttonsStyling: false,
          })
        }
      }
    },
    async enviarCorreoRecordatorio(actividadId, emails, archivoICS, evento) {
      try {
        const response = await this.$http.post('/email/recordatorio-actividad', {
          actividadId: actividadId,
          emailsDestino: emails, // Cambiar a array de emails
          archivoICS: archivoICS,
          evento: evento
        })
        
        if (response.data.success) {
          return Promise.resolve()
        } else {
          throw new Error(response.data.message || 'Error al enviar el correo')
        }
      } catch (error) {
        console.error('Error al enviar correo:', error)
        throw new Error('Error al enviar el recordatorio por correo')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.actividades-detalle {
  padding: 1.5rem;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.content-header-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.content-header-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

.info-item {
  margin-bottom: 1rem;
}

.info-item label {
  font-weight: 600;
  color: #495057;
  display: block;
  margin-bottom: 0.25rem;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s ease;
}

.file-item:hover {
  background-color: #f8f9fa;
}

.file-item:last-child {
  border-bottom: none;
}

.file-info {
  flex: 1;
  margin-left: 0.5rem;
}

.file-name {
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 0.25rem;
}

.file-meta {
  font-size: 0.875rem;
}

.file-actions {
  display: flex;
  gap: 0.5rem;
}

.project-name {
  font-weight: 600;
  color: #2c3e50;
}

@media (max-width: 768px) {
  .content-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .content-header-right {
    margin-top: 1rem;
  }
}
</style>
